import React, { useState } from "react";
import CustomModalV1 from "components/custom-modal/CustomModalV1";
import Button from "components/button/Button";
import { XIconV1 } from "images/image-component";
import "./redeem-modal.css";
import { useSelector } from "react-redux";
import { merchantMetadataSelector } from "store/userSliceHelpers";
import {
  CURRENCY_SIGN_BY_COUNTRY,
  CURRENCY_UNIT_BY_COUNTRY,
  SettlementCountry,
} from "types/money-settlement";

interface Props {
  isOpen: boolean;
  balance: number;
  tokenSymbol: string;
  minAmount: number;
  onClose: () => void;
  onConfirm: (amount: number) => void;
}

type Step = "input" | "confirm";

function RedeemModal({
  isOpen,
  balance,
  tokenSymbol,
  minAmount,
  onClose,
  onConfirm,
}: Props) {
  const [step, setStep] = useState<Step>("input");
  const [inputValue, setInputValue] = useState("");

  const amount = parseFloat(inputValue) || 0;
  const isValid = amount >= minAmount && amount <= balance;

  const merchantProfile = useSelector(merchantMetadataSelector);
  const currencyUnit =
    CURRENCY_UNIT_BY_COUNTRY[merchantProfile?.country as SettlementCountry];
  const currencySign =
    CURRENCY_SIGN_BY_COUNTRY[merchantProfile?.country as SettlementCountry];

  function handleClose() {
    setStep("input");
    setInputValue("");
    onClose();
  }

  function handleConfirm() {
    handleClose();
    onConfirm(amount);
  }

  return (
    <CustomModalV1 isOpen={isOpen} handleClose={handleClose}>
      <button
        className="v1-modal-close"
        onClick={handleClose}
        aria-label="Close"
      >
        <XIconV1 />
      </button>

      {step === "input" ? (
        <div className="rdm-modal">
          <h3 className="rdm-modal__title">Redeem Balance</h3>

          <div className="rdm-modal__available">
            <span className="rdm-modal__available-label">Available</span>
            <span className="rdm-modal__available-value">
              {balance.toFixed(2)}
              <span className="rdm-modal__available-unit"> {tokenSymbol}</span>
            </span>
          </div>

          <div className="rdm-modal__field">
            <label className="rdm-modal__label">Amount ({tokenSymbol})</label>
            <input
              className="rdm-modal__input"
              type="number"
              min={minAmount}
              max={balance}
              step="0.01"
              placeholder="0.00"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
            />
            <span className="rdm-modal__hint">
              Min {minAmount} {tokenSymbol} · Max {balance.toFixed(2)}{" "}
              {tokenSymbol}
            </span>
          </div>

          <div className="rdm-modal__euro-row">
            <span className="rdm-modal__euro-label">Equivalent</span>
            <span className="rdm-modal__euro-value">
              {currencySign}
              {amount.toFixed(2)} {currencyUnit}
            </span>
          </div>

          <div className="rdm-modal__actions">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!isValid}
              onClick={() => setStep("confirm")}
            >
              Continue
            </Button>
          </div>
        </div>
      ) : (
        <div className="rdm-modal">
          <h3 className="rdm-modal__title">Confirm Redeem</h3>

          <div className="rdm-modal__confirm-card">
            <div className="rdm-modal__confirm-row">
              <span className="rdm-modal__confirm-label">Amount</span>
              <span className="rdm-modal__confirm-vip">
                {amount.toFixed(2)} {tokenSymbol}
              </span>
            </div>
            <div className="rdm-modal__confirm-divider" />
            <div className="rdm-modal__confirm-row">
              <span className="rdm-modal__confirm-label">Equivalent</span>
              <span className="rdm-modal__confirm-euro">
                {currencySign}
                {amount.toFixed(2)} {currencyUnit}
              </span>
            </div>
          </div>

          <p className="rdm-modal__note">
            Payout goes to your registered bank account. Treasury approval
            required before transfer.
          </p>

          <div className="rdm-modal__actions">
            <Button variant="ghost" onClick={() => setStep("input")}>
              Back
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
              Confirm
            </Button>
          </div>
        </div>
      )}
    </CustomModalV1>
  );
}

export default RedeemModal;
